import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { 
  AlertTriangle, CheckCircle, Target, TrendingUp, 
  MessageSquare, ChevronDown, ChevronUp, Lightbulb,
  AlertCircle, Heart, BookOpen, Table, HelpCircle
} from 'lucide-react';

/**
 * CallAnalysis Component
 * Displays detailed call analysis for observer coaching (Principal/Observer view only)
 * NOT shown to parents - this is internal quality review
 * 
 * Renders AI-generated markdown analysis with structured sections:
 * - What Happened
 * - What Worked Well
 * - Joyful Moments Worth Celebrating
 * - Critical Moments Missed
 * - What Could Improve (structural analysis)
 * - Suggested Structure for Deeper Impact
 * - Practical Shifts (table)
 * - The Core Question
 * - Suggested Prompts for Next Call
 * - Cumulative Observations
 */

const CallAnalysis = ({
  childName = 'Child',
  observerName = 'Observer',
  date,
  compact = false,
  report = ''
}) => {
  const [expanded, setExpanded] = useState(!compact);
  
  // Handle both string and object formats for report
  const getReportText = () => {
    if (!report) return '';
    if (typeof report === 'string') return report;
    
    // If report is an object, convert to readable format
    const sections = [];
    if (report.overall_assessment) {
      sections.push(`### Overall Assessment\n${report.overall_assessment}`);
    }
    if (report.summary) {
      sections.push(`### Summary\n${report.summary}`);
    }
    if (report.scores) {
      const scoreText = Object.entries(report.scores)
        .map(([key, val]) => `- ${key.replace(/_/g, ' ')}: ${val}/10`)
        .join('\n');
      sections.push(`### Scores\n${scoreText}`);
    }
    if (report.strengths && report.strengths.length) {
      sections.push(`### What Worked Well\n${report.strengths.map(s => `- ${s}`).join('\n')}`);
    }
    if (report.areas_for_improvement && report.areas_for_improvement.length) {
      sections.push(`### Areas for Improvement\n${report.areas_for_improvement.map(s => `- ${s}`).join('\n')}`);
    }
    if (report.coaching_tips && report.coaching_tips.length) {
      sections.push(`### Coaching Tips\n${report.coaching_tips.map(s => `- ${s}`).join('\n')}`);
    }
    if (report.topics_identified && report.topics_identified.length) {
      sections.push(`### Topics Discussed\n${report.topics_identified.join(', ')}`);
    }
    return sections.join('\n\n');
  };
  
  // Parse sections from markdown report
  const parseSections = (text) => {
    if (!text) return [];
    
    const sections = [];
    const lines = text.split('\n');
    let currentSection = null;
    let currentContent = [];
    
    for (const line of lines) {
      // Check for section headers (### or ##)
      const headerMatch = line.match(/^#{2,3}\s+(.+)$/);
      if (headerMatch) {
        // Save previous section
        if (currentSection) {
          sections.push({
            title: currentSection,
            content: currentContent.join('\n').trim()
          });
        }
        currentSection = headerMatch[1].trim();
        currentContent = [];
      } else if (currentSection) {
        currentContent.push(line);
      }
    }
    
    // Don't forget the last section
    if (currentSection) {
      sections.push({
        title: currentSection,
        content: currentContent.join('\n').trim()
      });
    }
    
    return sections;
  };
  
  // Get icon and color for section
  const getSectionStyle = (title) => {
    const lowerTitle = title.toLowerCase();
    
    if (lowerTitle.includes('what happened')) {
      return { icon: BookOpen, color: 'blue', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' };
    }
    if (lowerTitle.includes('what worked')) {
      return { icon: CheckCircle, color: 'green', bgColor: 'bg-green-50', borderColor: 'border-green-200' };
    }
    if (lowerTitle.includes('joyful') || lowerTitle.includes('celebrating')) {
      return { icon: Heart, color: 'pink', bgColor: 'bg-pink-50', borderColor: 'border-pink-200' };
    }
    if (lowerTitle.includes('critical') || lowerTitle.includes('missed')) {
      return { icon: AlertTriangle, color: 'red', bgColor: 'bg-red-50', borderColor: 'border-red-200' };
    }
    if (lowerTitle.includes('could improve') || lowerTitle.includes('improvement')) {
      return { icon: TrendingUp, color: 'amber', bgColor: 'bg-amber-50', borderColor: 'border-amber-200' };
    }
    if (lowerTitle.includes('structure') || lowerTitle.includes('deeper impact')) {
      return { icon: Target, color: 'purple', bgColor: 'bg-purple-50', borderColor: 'border-purple-200' };
    }
    if (lowerTitle.includes('practical') || lowerTitle.includes('shift')) {
      return { icon: Table, color: 'indigo', bgColor: 'bg-indigo-50', borderColor: 'border-indigo-200' };
    }
    if (lowerTitle.includes('core question')) {
      return { icon: HelpCircle, color: 'rose', bgColor: 'bg-rose-50', borderColor: 'border-rose-200' };
    }
    if (lowerTitle.includes('prompt') || lowerTitle.includes('next call')) {
      return { icon: MessageSquare, color: 'teal', bgColor: 'bg-teal-50', borderColor: 'border-teal-200' };
    }
    if (lowerTitle.includes('cumulative') || lowerTitle.includes('observations')) {
      return { icon: TrendingUp, color: 'violet', bgColor: 'bg-violet-50', borderColor: 'border-violet-200' };
    }
    
    return { icon: Lightbulb, color: 'gray', bgColor: 'bg-gray-50', borderColor: 'border-gray-200' };
  };
  
  // Render markdown content with basic formatting
  const renderMarkdownContent = (content) => {
    if (!content) return null;
    
    // Split into paragraphs/blocks
    const blocks = content.split('\n\n').filter(b => b.trim());
    
    return blocks.map((block, idx) => {
      // Check for tables
      if (block.includes('|') && block.includes('---')) {
        return renderTable(block, idx);
      }
      
      // Check for numbered lists (1. 2. etc)
      if (/^\d+\.\s/.test(block.trim())) {
        return renderNumberedList(block, idx);
      }
      
      // Check for bullet lists
      if (/^[-*]\s/.test(block.trim())) {
        return renderBulletList(block, idx);
      }
      
      // Check for blockquotes
      if (block.trim().startsWith('>')) {
        return renderBlockquote(block, idx);
      }
      
      // Regular paragraph with inline formatting
      return (
        <p key={idx} className="text-sm text-gray-700 mb-3 leading-relaxed">
          {renderInlineFormatting(block)}
        </p>
      );
    });
  };
  
  // Render inline formatting (bold, italic, code)
  const renderInlineFormatting = (text) => {
    // Split by bold markers
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    
    return parts.map((part, idx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={idx} className="font-semibold text-gray-900">{part.slice(2, -2)}</strong>;
      }
      // Handle inline code
      if (part.includes('`')) {
        const codeParts = part.split(/(`[^`]+`)/g);
        return codeParts.map((cp, cIdx) => {
          if (cp.startsWith('`') && cp.endsWith('`')) {
            return <code key={`${idx}-${cIdx}`} className="bg-gray-100 px-1 rounded text-sm">{cp.slice(1, -1)}</code>;
          }
          return cp;
        });
      }
      return part;
    });
  };
  
  // Render bullet list
  const renderBulletList = (block, key) => {
    const items = block.split('\n').filter(l => l.trim());
    return (
      <ul key={key} className="space-y-2 mb-3">
        {items.map((item, idx) => {
          const text = item.replace(/^[-*]\s+/, '').trim();
          return (
            <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
              <span className="text-gray-400 mt-1">•</span>
              <span>{renderInlineFormatting(text)}</span>
            </li>
          );
        })}
      </ul>
    );
  };
  
  // Render numbered list
  const renderNumberedList = (block, key) => {
    const items = block.split('\n').filter(l => l.trim());
    return (
      <ol key={key} className="space-y-2 mb-3 list-decimal list-inside">
        {items.map((item, idx) => {
          const text = item.replace(/^\d+\.\s+/, '').trim();
          return (
            <li key={idx} className="text-sm text-gray-700">
              {renderInlineFormatting(text)}
            </li>
          );
        })}
      </ol>
    );
  };
  
  // Render blockquote
  const renderBlockquote = (block, key) => {
    const text = block.split('\n').map(l => l.replace(/^>\s*/, '')).join(' ');
    return (
      <blockquote key={key} className="border-l-4 border-gray-300 pl-4 py-2 mb-3 bg-gray-50 rounded-r italic text-sm text-gray-600">
        "{text.trim()}"
      </blockquote>
    );
  };
  
  // Render markdown table
  const renderTable = (block, key) => {
    const lines = block.split('\n').filter(l => l.trim() && !l.includes('---'));
    if (lines.length < 2) return null;
    
    const headers = lines[0].split('|').filter(c => c.trim()).map(c => c.trim());
    const rows = lines.slice(1).map(line => 
      line.split('|').filter(c => c.trim()).map(c => c.trim())
    );
    
    return (
      <div key={key} className="overflow-x-auto mb-4">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-100">
              {headers.map((h, idx) => (
                <th key={idx} className="border border-gray-200 px-3 py-2 text-left font-semibold text-gray-800">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rIdx) => (
              <tr key={rIdx} className={rIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                {row.map((cell, cIdx) => (
                  <td key={cIdx} className="border border-gray-200 px-3 py-2 text-gray-700">
                    {renderInlineFormatting(cell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };
  
  const sections = parseSections(getReportText());
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Today';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };
  
  const firstName = childName?.split(' ')[0] || 'Child';
  
  // Compact collapsed view
  if (compact && !expanded) {
    const whatHappened = sections.find(s => s.title.toLowerCase().includes('what happened'));
    return (
      <div 
        className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-3 border border-amber-200 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => setExpanded(true)}
      >
        <div className="flex items-center justify-between">
          <h5 className="font-medium text-amber-800 text-sm flex items-center gap-2">
            <Target className="w-4 h-4" />
            Call Analysis: {firstName}
          </h5>
          <div className="flex items-center gap-2">
            <span className="text-xs text-amber-600">{formatDate(date)}</span>
            <ChevronDown className="w-4 h-4 text-amber-500" />
          </div>
        </div>
        {whatHappened && (
          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{whatHappened.content.substring(0, 150)}...</p>
        )}
        <div className="mt-2 flex flex-wrap gap-2">
          <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">
            {sections.length} sections
          </span>
        </div>
      </div>
    );
  }
  
  return (
    <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 overflow-hidden">
      <CardContent className="p-0">
        {/* Header */}
        <div 
          className="bg-gradient-to-r from-amber-600 to-orange-600 px-4 py-3 text-white cursor-pointer"
          onClick={() => compact && setExpanded(!expanded)}
        >
          <div className="flex items-center justify-between">
            <h4 className="font-semibold flex items-center gap-2">
              <Target className="w-5 h-5" />
              Call Analysis: {formatDate(date)}
            </h4>
            {compact && <ChevronUp className="w-5 h-5" />}
          </div>
          <p className="text-amber-100 text-sm mt-1">
            {firstName} with {observerName} • Internal Coaching Review
          </p>
        </div>
        
        {/* Sections */}
        <div className="p-4 space-y-4">
          {sections.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <AlertCircle className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p>No analysis content available</p>
            </div>
          ) : (
            sections.map((section, idx) => {
              const style = getSectionStyle(section.title);
              const Icon = style.icon;
              
              return (
                <div key={idx} className={`rounded-lg p-4 ${style.bgColor} border ${style.borderColor}`}>
                  <h5 className={`font-semibold mb-3 flex items-center gap-2 text-${style.color}-800`}>
                    <Icon className={`w-4 h-4 text-${style.color}-600`} />
                    {section.title}
                  </h5>
                  <div className="prose prose-sm max-w-none">
                    {renderMarkdownContent(section.content)}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CallAnalysis;
